import {
  ColumnDef,
  ExpandedState,
  getCoreRowModel,
  OnChangeFn,
  RowSelectionState,
  SortingState,
  useReactTable
} from "@tanstack/react-table";
import { Checkbox } from "shared/components";

import { Table } from "shared/app/Table";
import {
  Archive,
  ArrowRight,
  File06,
  FileX03,
  FlipBackward,
  SearchSM,
  User01,
  Users01
} from "untitledui-js-base";
import { useLocation, useNavigate } from "react-router-dom";
import { ApplicationFeeStatus, ApplicationStatus, Invitee } from "shared/types";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useState
} from "react";
import moment from "moment";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { ApplicationStatusBadge } from "shared/app/ApplicationStatusBadge";
import { Applicant, Application } from "types/application";

import { useAuthContext } from "providers/Auth";
import { RuleBaseGraph } from "shared/app/RuleBaseGraph";
import { ScoreGraphSize } from "views/applications-id/components/ApplicationScoreCard/constants";
import { ScoreGraph } from "views/applications-id/components/ApplicationScoreCard/ScoreGraph";
import { getFullName } from "shared/utils/get-full-name";
import { ActiveTab } from "../../index";

export const TableApplications = ({
  applications,
  onInviteClick,
  activeTab,
  hasSearchParams,
  isLoading,
  setSelectedRentersRow,
  selectedRentersRow,
  onCancelClick,
  onMoveClick,
  onRefundClick,
  sorting,
  setSorting,
  sortableKeys
}: {
  applications: Application[];
  onInviteClick?: () => void;
  activeTab: ActiveTab;
  isPending?: boolean;
  isCanceled?: boolean;
  hasSearchParams?: boolean;
  setSelectedRentersRow: OnChangeFn<RowSelectionState>;
  selectedRentersRow: RowSelectionState;
  onCancelClick?: (id: string) => void;
  onMoveClick?: (id: string) => void;
  onRefundClick?: (id: string) => void;
  isLoading?: boolean;
  sorting: SortingState;
  setSorting: Dispatch<SetStateAction<SortingState>>;
  sortableKeys: string[];
  invalidateQueries?: unknown[];
}) => {
  const { pathname } = useLocation();
  const { type, permissions } = useAuthContext();
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const navigate = useNavigate();

  const currentTab = {
    isAll: activeTab === ActiveTab.All,
    isPending: activeTab === ActiveTab.Pending,
    isStarted: activeTab === ActiveTab.Started,
    isInvited: activeTab === ActiveTab.Invited,
    isApproved: activeTab === ActiveTab.Approved,
    isConditionallyApproved: activeTab === ActiveTab.ApprovedConditionally,
    isDeclined: activeTab === ActiveTab.Declined,
    isCanceled: activeTab === ActiveTab.Canceled
  };

  useEffect(() => {
    // reset expanded state when applications change
    setExpanded({});
  }, [applications]);

  const columns = useMemo(() => {
    const ret: ColumnDef<Application, Application>[] = [
      {
        id: "fixed_width checkbox custom_",
        header: "",
        size: 40,
        cell: ({ row }) => (
          <div className="pl-[15px]" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler()
              }}
            />
          </div>
        )
      },
      {
        accessorFn: (row) => row,
        id: "applicant_score custom_ fixed_width",
        header: "Criteria",
        size: 80,
        cell: (p) => {
          const val = p.getValue();
          const templateCriteriaSettings =
            val.verification_template?.screening_criteria;

          const hasValue = [
            ApplicationStatus.Submitted,
            ApplicationStatus.Approved,
            ApplicationStatus.Rejected,
            ApplicationStatus.ConditionalApproved,
            ApplicationStatus.Canceled
          ].includes(val.status);

          if (
            !val.verification_template?.recommendation_method ||
            val.verification_template.recommendation_method === "none"
          )
            return null;

          if (val.verification_template.recommendation_method === "score_based")
            return (
              <div className="overflow-hidden pl-2">
                <ScoreGraph
                  size={ScoreGraphSize.XS}
                  score={hasValue ? val.score : null}
                  declineRangeEnd={templateCriteriaSettings?.decline_range_end}
                  conditionalApprovalRangeEnd={
                    templateCriteriaSettings?.conditional_approval_range_end
                  }
                />
              </div>
            );

          return (
            <div className="overflow-hidden mx-auto flex w-full 2xl:justify-center 2xl:pl-0 justify-center">
              <RuleBaseGraph
                recommendation={hasValue ? val.recommendation : null}
              />
            </div>
          );
        }
      },
      {
        id: "id",
        accessorFn: (row) => row,
        header: "Applicants",
        size: 180,
        cell: (p) => {
          const { applicants, pending_magic_links } = p.getValue();

          const total_applicants =
            (applicants.length || 0) + (pending_magic_links?.length || 0);

          const applicantNames = applicants.length
            ? applicants
                .map((applicant) =>
                  getFullName({
                    firstName: applicant.first_name,
                    lastName: applicant.last_name,
                    middleName: applicant.middle_name,
                    short: true
                  })
                )
                .join(", ")
            : "";

          const inviteesEmails = (pending_magic_links || [])
            .map((ml) => ml.email)
            .join(", ");

          return (
            <p className="m-0 flex w-full items-center gap-3 2xl:max-w-[400px] ">
              {total_applicants > 1 ? (
                <Users01
                  size="20"
                  className="inline shrink-0 stroke-content-300"
                />
              ) : (
                <User01
                  size="20"
                  className="inline shrink-0 stroke-content-300"
                />
              )}
              <span className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-medium text-content-1000">
                {total_applicants > 1 ? `(${total_applicants}) ` : null}
                {applicantNames && inviteesEmails
                  ? `${applicantNames}, ${inviteesEmails}`
                  : applicantNames
                    ? applicantNames
                    : inviteesEmails}
              </span>
            </p>
          );
        }
      }
    ];

    if (!pathname.includes("properties"))
      ret.push({
        accessorKey: "property.address.address1",
        header: "Property",
        size: 100,
        cell: (p) => {
          const unit = p.row.original;
          return (
            <span className="block 2xl:max-w-none">
              <span className="block overflow-hidden text-ellipsis  whitespace-nowrap text-sm font-medium text-content-1000">
                {unit.property.name}
              </span>
              <span className="block overflow-hidden text-ellipsis whitespace-nowrap text-xs text-content-400">
                {
                  p.getValue() as unknown as Application["property"]["address"]["address1"]
                }
              </span>
            </span>
          );
        }
      });

    if (!pathname.includes("units"))
      ret.push(
        ...[
          {
            accessorKey: "unit.title",
            header: "Unit",
            size: 80,
            cell: (p) => {
              return (
                <span className="block overflow-hidden text-ellipsis whitespace-nowrap 2xl:max-w-[140px] max-w-[80px]">
                  {p.getValue()}
                </span>
              );
            }
          }
        ]
      );

    const submittedAtColumn = {
      accessorKey: "submitted_at",
      header: "Submitted",
      id: "submitted_at",
      cell: (p) => {
        if (!p.getValue()) return "--";
        return moment(p.getValue()).format("ll [at] hh:mm A");
      }
    };
    const startedAtColumn = {
      accessorKey: "started_at",
      header: "Started",
      id: "started_at",
      cell: (p) => {
        if (!p.getValue()) return "--";
        return moment(p.getValue()).format("ll [at] hh:mm A");
      }
    };

    if (currentTab.isStarted) ret.push(startedAtColumn);
    else if (!currentTab.isInvited)
      ret.push(...[startedAtColumn, submittedAtColumn]);

    ret.push(
      ...[
        {
          accessorKey: "status",
          header: "Status",
          id: "status",
          size: 130,
          cell: (p) => {
            const { conditions, status, has_processing_verification } =
              p.row.original;
            const applicationStatus =
              has_processing_verification &&
              status === ApplicationStatus.Submitted
                ? ApplicationStatus.Processing
                : status;
            return (
              <div className="flex w-full">
                <ApplicationStatusBadge
                  status={applicationStatus as ApplicationStatus}
                  conditions={conditions}
                  type="application"
                />
              </div>
            );
          }
        },
        {
          id: "popover-action",
          size: 48,
          accessorFn: (row) => row.id,
          header: "",
          cell: (p) => {
            const id = p.getValue();
            const status = p.row.original.status;
            const actions: {
              title: string | JSX.Element;
              onClick: () => void;
              disabled?: boolean;
              tooltip?: string;
            }[] = [
              {
                title: "Review",
                onClick: () => {
                  navigate(`/applications/${id}`);
                }
              }
            ];

            if (type.isAdmin || type.isOwner || permissions.canChangeUnit)
              actions.push({
                title: (
                  <span className="flex gap-2 items-center">
                    <ArrowRight size="18" className="stroke-content-200" />
                    Move to another unit
                  </span>
                ),
                tooltip:
                  "You can't move application to another unit once the decision have been made",
                disabled: [
                  ApplicationStatus.Canceled,
                  ApplicationStatus.Approved,
                  ApplicationStatus.Rejected,
                  ApplicationStatus.ConditionalApproved
                ].includes(p.row.original.status),
                onClick: () => {
                  if (
                    ![
                      ApplicationStatus.Canceled,
                      ApplicationStatus.Approved,
                      ApplicationStatus.Rejected,
                      ApplicationStatus.ConditionalApproved
                    ].includes(p.row.original.status)
                  )
                    onMoveClick && onMoveClick(id);
                }
              });

            if (
              ApplicationStatus.Canceled !== status &&
              (type.isAdmin ||
                type.isOwner ||
                permissions.canCancelApplications)
            )
              actions.push({
                title: (
                  <span className="flex gap-2 items-center">
                    <Archive size="18" className="stroke-content-200" />
                    Cancel application
                  </span>
                ),
                tooltip:
                  p.row.original.status === ApplicationStatus.Canceled
                    ? "Cannot cancel a canceled application"
                    : "You can't cancel application once the decisions have been made",
                disabled: [
                  ApplicationStatus.Canceled,
                  ApplicationStatus.Approved,
                  ApplicationStatus.Rejected,
                  ApplicationStatus.ConditionalApproved
                ].includes(p.row.original.status),
                onClick: () => {
                  if (
                    ![
                      ApplicationStatus.Canceled,
                      ApplicationStatus.Approved,
                      ApplicationStatus.Rejected,
                      ApplicationStatus.ConditionalApproved
                    ].includes(p.row.original.status)
                  )
                    onCancelClick && onCancelClick(id);
                }
              });

            if (type.isAdmin || type.isOwner) {
              const { applicants } = p.row.original;

              actions.push({
                title: (
                  <span className="flex gap-2 items-center">
                    <FlipBackward size="18" className="stroke-content-200" />
                    Refund application
                  </span>
                ),
                disabled: !applicants.some(
                  (applicant: Applicant) =>
                    applicant.application_fee_status ===
                      ApplicationFeeStatus.Paid &&
                    applicant.application_status === ApplicationStatus.Submitted
                ),
                tooltip: applicants.some(
                  (applicant: Applicant) =>
                    applicant.application_status === ApplicationStatus.Submitted
                )
                  ? applicants.every(
                      (applicant: Applicant) =>
                        applicant.application_fee_status ===
                        ApplicationFeeStatus.Refunded
                    )
                    ? "Refund has already been requested for this application"
                    : applicants.some(
                          (applicant: Applicant) =>
                            applicant.application_fee_status ===
                            ApplicationFeeStatus.Paid
                        )
                      ? ""
                      : "You can request a refund if someone has paid an application fee"
                  : "You can request a refund for submitted applications",
                onClick: () => onRefundClick && onRefundClick(id)
              });
            }

            return <ContextMenuPopover actions={actions} />;
          }
        }
      ]
    );

    return ret;
  }, [currentTab]);

  const table = useReactTable({
    defaultColumn: {
      size: 100,
      maxSize: 500,
      minSize: 20
    },
    data: applications,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      expanded,
      sorting,
      rowSelection: selectedRentersRow
    },
    enableRowSelection: true,
    manualSorting: true,
    enableMultiSort: false,
    onSortingChange: setSorting,
    // Something is weird
    getSubRows: (rows: Application) => rows as unknown as Application[],
    onRowSelectionChange: setSelectedRentersRow,
    onExpandedChange: setExpanded,
    getRowId: (row) => String(row.id)
  });

  return (
    <Table
      rowHeight="64px"
      setSorted={setSorting}
      sorted={sorting}
      sortableKeys={sortableKeys}
      isLoading={isLoading}
      emptyStateTitle={
        hasSearchParams
          ? "No results found"
          : currentTab.isAll
            ? "Applications will appear here"
            : `No ${currentTab.isConditionallyApproved ? "conditionally approved" : activeTab.toLowerCase()} applications`
      }
      emptyStateText={
        hasSearchParams
          ? "We couldn't find any data matching your query"
          : currentTab.isAll
            ? "Invite applicants to create an application"
            : currentTab.isPending
              ? "Applications awaiting a decision will appear here"
              : `${currentTab.isConditionallyApproved ? "Conditionally approved" : activeTab} applications will appear here`
      }
      emptyStateButtonText={!currentTab.isAll ? undefined : "Invite applicants"}
      emptyStateButtonAction={
        currentTab.isAll
          ? () => {
              onInviteClick && onInviteClick();
            }
          : undefined
      }
      EmptyStateIcon={
        hasSearchParams
          ? SearchSM
          : currentTab.isPending || currentTab.isAll
            ? File06
            : FileX03
      }
      table={table}
      onRowClick={(id) => navigate(`/applications/${id}`)}
      mobileRowClassName="max-w-[calc(100vw-1.5rem)]"
      mobileRow={(row: {
        original: Application;
        getIsSelected: () => boolean;
        getCanSelect: () => boolean;
        getIsSomeSelected: () => boolean;
        getToggleSelectedHandler: () => () => void;
      }) => {
        const applicants: (Invitee | Applicant)[] =
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          (row.original.applicants || [])
            // @ts-expect-error
            .concat(row.original.pending_magic_links || []);
        const names = applicants
          .map((applicant) =>
            "first_name" in applicant
              ? getFullName({
                  firstName: applicant.first_name,
                  lastName: applicant.last_name,
                  middleName: applicant.middle_name,
                  short: true
                })
              : applicant.email
          )
          .join(", ");
        return (
          <div>
            <div className="flex items-center justify-between">
              <div
                className="h-5 -translate-y-5"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <Checkbox
                  {...{
                    checked: row.getIsSelected(),
                    disabled: !row.getCanSelect(),
                    indeterminate: row.getIsSomeSelected(),
                    onChange: row.getToggleSelectedHandler()
                  }}
                  label={
                    <div>
                      {((): ReactNode => {
                        const val = row.original;
                        const templateCriteriaSettings =
                          val.verification_template?.screening_criteria;

                        const hasValue = [
                          ApplicationStatus.Submitted,
                          ApplicationStatus.Approved,
                          ApplicationStatus.Rejected,
                          ApplicationStatus.ConditionalApproved,
                          ApplicationStatus.Canceled
                        ].includes(val.status);

                        if (
                          !val.verification_template?.recommendation_method ||
                          val.verification_template.recommendation_method ===
                            "none"
                        )
                          return null;

                        if (
                          val.verification_template.recommendation_method ===
                          "score_based"
                        )
                          return (
                            <div className="overflow-hidden">
                              <ScoreGraph
                                size={ScoreGraphSize.XS}
                                score={hasValue ? val.score : null}
                                declineRangeEnd={
                                  templateCriteriaSettings?.decline_range_end
                                }
                                conditionalApprovalRangeEnd={
                                  templateCriteriaSettings?.conditional_approval_range_end
                                }
                              />
                            </div>
                          );

                        return (
                          <div className="overflow-hidden mx-auto flex w-full 2xl:justify-center 2xl:pl-0 justify-center">
                            <RuleBaseGraph
                              recommendation={
                                hasValue ? val.recommendation : null
                              }
                            />
                          </div>
                        );
                      })()}
                    </div>
                  }
                />
              </div>

              <ContextMenuPopover
                triggerClassName="translate-x-2"
                actions={[
                  {
                    title: "Review",
                    onClick: () => {
                      navigate(`/applications/${row.original.id}`);
                    }
                  }
                ]}
              />
            </div>

            <div className="flex flex-col w-full">
              <div className="flex w-full items-center gap-2">
                <p className="text-sm font-medium text-content-1000">
                  {applicants.length > 1
                    ? `(${row.original.applicants.length}) `
                    : ""}
                  {names}
                </p>
              </div>
              <div className="flex w-full items-center gap-2">
                <p className="text-xs text-content-400">
                  {row.original.property.address.address1} /{" "}
                  {row.original.unit.title}
                </p>
              </div>
            </div>

            <div className="mt-2 ml-auto">
              <ApplicationStatusBadge
                status={row.original.status}
                type="application"
              />
            </div>
          </div>
        );
      }}
      // expandedRenderer={(row) => {
      //   const data = row.originalSubRows as unknown as Application | undefined;
      //   if (!data)
      //     return (
      //       <div className="animate-appear flex w-full flex-wrap gap-4 bg-background-900">
      //         <div className="w-full max-w-[270px] rounded-lg border border-alpha-100 bg-white p-6 shadow-xs">
      //           <h2 className="line-clamp-1 text-ellipsis text-sm font-bold text-content-1000">
      //             No data
      //           </h2>
      //         </div>
      //       </div>
      //     );
      //   return (
      //     <div className="flex-wrap flex gap-2 p-4">
      //       {(data.applicants || [])
      //         .concat((data.pending_magic_links || []) as Applicant[])
      //         .map((applicant: Applicant | Invitee) => {
      //           return (
      //             <ApplicantCard
      //               applicationStatus={data.status}
      //               refunded={data.refunded}
      //               invalidateQueries={invalidateQueries}
      //               key={
      //                 "expanded_application_" +
      //                 data.id +
      //                 "_applicant" +
      //                 applicant.id
      //               }
      //               applicationId={String(data.id)}
      //               applicant={applicant}
      //               criteriaEnabled={Boolean(
      //                 data.verification_template?.recommendation_method ===
      //                   "score_based"
      //               )}
      //               templateCriteriaSettings={
      //                 data.verification_template?.screening_criteria
      //               }
      //             />
      //           );
      //         })}
      //     </div>
      //   );
      // }}
    />
  );
};
